/**
 * Extrae el valor escalar de una celda ExcelJS (rich text, fórmula, booleano, etc.).
 */
export function normalizarValorCeldaExcel(valor) {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'boolean' || typeof valor === 'number') return valor;
  if (valor instanceof Date) return valor;
  if (typeof valor === 'object') {
    if (Array.isArray(valor.richText)) {
      return valor.richText.map((parte) => parte.text ?? '').join('');
    }
    if (valor.text !== undefined && valor.text !== null) return valor.text;
    if (valor.result !== undefined && valor.result !== null) return valor.result;
    if (valor.hyperlink && valor.text !== undefined) return valor.text;
  }
  return valor;
}

/**
 * Normaliza fechas leídas desde Excel (serial, Date, DD-MM-YYYY, YYYY-MM-DD).
 */
export function normalizarFechaImportacion(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    const y = valor.getFullYear();
    const m = String(valor.getMonth() + 1).padStart(2, '0');
    const d = String(valor.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  if (typeof valor === 'string') {
    const trimmed = valor.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const dmy = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmy) {
      const day = Number(dmy[1]);
      const month = Number(dmy[2]);
      const year = Number(dmy[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }

  const num = Number(valor);
  if (!Number.isNaN(num) && num > 0 && num < 1000000) {
    const excelEpochUtc = Date.UTC(1899, 11, 30);
    const date = new Date(excelEpochUtc + Math.floor(num) * 86400000);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const str = String(valor).trim();
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return null;
}

export function extraerFechaYMD(valor) {
  if (valor == null || valor === '') return null;
  const s = String(valor).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return null;
}

export function obtenerRegistrosHistorial(empleado) {
  const historial = empleado?.historial_laboral || empleado?.historialLaboral;
  const registros = Array.isArray(historial) && historial.length > 0
    ? historial
    : (empleado?.ultimo_historial ? [empleado.ultimo_historial] : []);

  return depurarRegistrosHistorial(registros, empleado);
}

/**
 * Elimina registros abiertos duplicados y, si el empleado está cesado,
 * solo considera periodos con fecha de cese.
 */
export function depurarRegistrosHistorial(registros, empleado = null) {
  if (!Array.isArray(registros) || registros.length === 0) return [];

  const ingresosConCese = new Set(
    registros
      .filter((h) => h?.fecha_cese)
      .map((h) => extraerFechaYMD(h.fecha_ingreso))
      .filter(Boolean)
  );

  let depurados = registros.filter((h) => {
    const ingreso = extraerFechaYMD(h?.fecha_ingreso);
    if (!ingreso) return false;
    if (!h.fecha_cese && ingresosConCese.has(ingreso)) {
      return false;
    }
    return true;
  });

  const estado = String(empleado?.estado || '').trim().toLowerCase();
  if (estado === 'cesado') {
    const conCese = depurados.filter((h) => h.fecha_cese);
    if (conCese.length > 0) {
      depurados = conCese;
    }
  }

  return depurados;
}

/**
 * Misma lógica que Empleado::getPeriodoVigenteEnFecha (backend).
 */
export function obtenerPeriodoVigenteEnFecha(empleado, fechaStr) {
  const fecha = extraerFechaYMD(fechaStr);
  if (!fecha || !empleado) return null;

  const registros = obtenerRegistrosHistorial(empleado);
  if (!registros.length) return null;

  const vigente = registros
    .filter((h) => h?.fecha_ingreso && extraerFechaYMD(h.fecha_ingreso) <= fecha)
    .sort((a, b) => {
      const ia = extraerFechaYMD(a.fecha_ingreso);
      const ib = extraerFechaYMD(b.fecha_ingreso);
      if (ib !== ia) return ib.localeCompare(ia);
      return (b.id || 0) - (a.id || 0);
    })[0];

  if (!vigente) return null;

  if (vigente.fecha_cese && extraerFechaYMD(vigente.fecha_cese) < fecha) {
    return null;
  }

  return vigente;
}

/**
 * Período laboral que solapa con el rango de la planilla (mayor fecha_ingreso entre solapados).
 */
export function obtenerPeriodoLaboralEnRango(empleado, fechaInicio, fechaFin) {
  const inicio = extraerFechaYMD(fechaInicio);
  const fin = extraerFechaYMD(fechaFin);
  if (!inicio || !fin || !empleado) return null;

  const solapados = obtenerRegistrosHistorial(empleado).filter((h) => {
    if (!h?.fecha_ingreso) return false;
    const ingreso = extraerFechaYMD(h.fecha_ingreso);
    const cese = h.fecha_cese ? extraerFechaYMD(h.fecha_cese) : null;
    return ingreso <= fin && (!cese || cese >= inicio);
  });

  if (!solapados.length) return null;

  return solapados.sort((a, b) => {
    const ia = extraerFechaYMD(a.fecha_ingreso);
    const ib = extraerFechaYMD(b.fecha_ingreso);
    if (ib !== ia) return ib.localeCompare(ia);
    return (b.id || 0) - (a.id || 0);
  })[0];
}

export function obtenerFechasLaboralesEfectivasEnRango(empleado, fechaInicioPlanilla, fechaFinPlanilla) {
  const inicioPlanilla = extraerFechaYMD(fechaInicioPlanilla);
  const finPlanilla = extraerFechaYMD(fechaFinPlanilla);
  const periodo = obtenerPeriodoLaboralEnRango(empleado, inicioPlanilla, finPlanilla);

  if (!periodo) {
    return { fechaIngreso: null, fechaCese: null, periodo: null };
  }

  const ingreso = extraerFechaYMD(periodo.fecha_ingreso);
  const cese = periodo.fecha_cese ? extraerFechaYMD(periodo.fecha_cese) : null;
  const fechaIngresoEfectiva = ingreso > inicioPlanilla ? ingreso : inicioPlanilla;
  let fechaCeseEfectiva = finPlanilla;
  if (cese && cese < finPlanilla) {
    fechaCeseEfectiva = cese;
  }

  return {
    fechaIngreso: fechaIngresoEfectiva,
    fechaCese: fechaCeseEfectiva,
    periodo,
  };
}

export function empleadoActivoDuranteRango(empleado, fechaInicio, fechaFin) {
  const periodo = obtenerPeriodoLaboralEnRango(empleado, fechaInicio, fechaFin);
  if (!periodo) return false;
  if (periodo.motivo_cese === 'No se presentó a laborar') return false;

  const { fechaIngreso, fechaCese } = obtenerFechasLaboralesEfectivasEnRango(
    empleado,
    fechaInicio,
    fechaFin
  );
  if (!fechaIngreso || !fechaCese || fechaIngreso > fechaCese) return false;

  return true;
}

/**
 * Arma historial laboral desde snapshot de planilla cerrada cuando no viene historial_laboral completo.
 */
export function construirEmpleadoParaHistorialLaboral(empleado) {
  if (!empleado) return empleado;
  if (obtenerRegistrosHistorial(empleado).length > 0) return empleado;

  const fechaIngreso = empleado.fecha_ingreso_actual
    || empleado.fechaIngreso
    || empleado.fecha_ingreso;
  const fechaCese = empleado.fecha_cese_actual
    || empleado.fechaCese
    || empleado.fecha_cese
    || null;

  if (!fechaIngreso) return empleado;

  return {
    ...empleado,
    historial_laboral: [{
      id: 0,
      fecha_ingreso: fechaIngreso,
      fecha_cese: fechaCese,
    }],
  };
}

export function tareoEnPeriodoLaboralEfectivo(fechaTareo, empleado, fechaInicioPlanilla, fechaFinPlanilla) {
  const fecha = extraerFechaYMD(fechaTareo);
  if (!fecha) return false;

  const { fechaIngreso, fechaCese } = obtenerFechasLaboralesEfectivasEnRango(
    empleado,
    fechaInicioPlanilla,
    fechaFinPlanilla
  );

  if (!fechaIngreso || !fechaCese) return false;
  return fecha >= fechaIngreso && fecha <= fechaCese;
}

/**
 * Misma lógica que Empleado::getPeriodoVigenteEnFecha (backend).
 */
export function empleadoActivoEnFechaHistorial(empleado, fechaStr) {
  if (!fechaStr || !empleado) return false;
  return obtenerPeriodoVigenteEnFecha(empleado, fechaStr) !== null;
}
